import { ReactNode, useState, useEffect } from 'react';
import { subscribeUI } from 'utils/uiStore';
import Header from 'components/ui/Header';
import Sidebar from 'components/ui/Sidebar';
import Breadcrumb from 'components/ui/Breadcrumb';
import { TourProvider } from 'contexts/TourContext';
import TourEngine from 'components/tour/TourEngine';
import NetworkStatusBar from 'components/ui/NetworkStatusBar';

interface PageHeaderProps {
  title: string;
  description?: string;
  actions?: ReactNode;
}

export const PageHeader = ({ title, description, actions }: PageHeaderProps) => {
  return (
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-8">
      <div>
        <h1 className="text-xl sm:text-2xl font-semibold text-text-primary mb-1 sm:mb-2 leading-snug">{title}</h1>
        {description && <p className="text-sm sm:text-base text-text-secondary">{description}</p>}
      </div>

      {actions && <div className="flex items-center space-x-3 mt-4 sm:mt-0">{actions}</div>}
    </div>
  );
};

interface MainLayoutProps {
  children: ReactNode;
}

const MainLayout = ({ children }: MainLayoutProps) => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  useEffect(() => {
    const unsub = subscribeUI(s => setSidebarCollapsed(s.sidebarCollapsed));
    return () => unsub();
  }, []);

  // Padding matches sidebar exact width (w-24=6rem, w-12=3rem) to remove extra gap
  const paddingClass = sidebarCollapsed ? 'md:pl-14' : 'md:pl-24';

  return (
    <TourProvider>
      <TourEngine />
      <NetworkStatusBar />
      <div className="min-h-screen bg-background flex overflow-hidden">
        <Sidebar />
        <div className={`flex-1 ${paddingClass} pt-16 transition-all overflow-y-auto h-screen`}>
          <Header />
          <Breadcrumb />
          <main className="pt-4 pb-8">
            <div className="w-full mx-auto px-3 sm:px-4 md:px-6">{children}</div>
          </main>
        </div>
      </div>
    </TourProvider>
  );
};

export default MainLayout;
